/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Junie (AI), 24-06-2026
 *
 * Tussenklasse voor Angular Island-componenten die aan een Cytoscape-node
 * gekoppeld zijn (prullenbak, menu, label, en later overige overlay-componenten).
 * Houdt koppel/ontkoppel van de node bij en exposeert de selectie-state als
 * signal, zodat templates met `OnPush`/zoneless automatisch reageren.
 */
import {Directive, Input, OnChanges, OnDestroy, signal} from '@angular/core';
import type {Core, NodeSingular} from 'cytoscape';
import {BaseIslandComponent} from '../base-island.component';

@Directive()
export abstract class BaseNodeGekoppeldComponent extends BaseIslandComponent implements OnChanges, OnDestroy {

    /**
     * De Cytoscape-node waaraan dit component gekoppeld is.
     */
    @Input({required: true}) node!: NodeSingular;

    /**
     * De Cytoscape-`Core` waarin de node leeft. Doorgegeven aan `[dlNodeVolger]`.
     */
    @Input({required: true}) cy!: Core;

    /**
     * Selectie-state van de gekoppelde node. Signal, zodat de template reactief
     * meeloopt zonder expliciete change detection-aanroepen.
     */
    protected readonly geselecteerd = signal(false);

    private gekoppeldeNode: NodeSingular | null = null;

    private readonly onSelectie = (): void => {
        this.geselecteerd.set(!!this.gekoppeldeNode?.selected());
    };

    /**
     * Hercoupleert de selectie-listener wanneer de node-input verandert.
     */
    ngOnChanges(): void {
        if (this.gekoppeldeNode !== this.node) {
            this.ontkoppel();
            this.koppel();
        }
    }

    /**
     * Ruimt event-listeners op bij verwijdering.
     */
    ngOnDestroy(): void {
        this.ontkoppel();
    }

    /**
     * Koppelt de selectie-listener direct op de node en synchroniseert
     * de signal-state met de huidige selectie. Door op `this.node` te
     * luisteren (en niet via cy met id-selector) blijft de code simpel;
     * de canvas debouncet de nodes-array zodat cy de state heeft afgerond
     * voordat dit component aangemaakt wordt.
     */
    private koppel(): void {
        if (!this.node) return;
        this.gekoppeldeNode = this.node;
        this.geselecteerd.set(this.node.selected());
        (this.gekoppeldeNode as any).on('select unselect', this.onSelectie)
    }

    /**
     * Verwijdert de eerder gekoppelde selectie-listener.
     */
    private ontkoppel(): void {
        if (this.gekoppeldeNode) {
            (this.gekoppeldeNode as any).off('select unselect', this.onSelectie);
            this.gekoppeldeNode = null;
        }
    }
}
